/**
 * generationTimeout.js — F-334/F-335. COMPLETE OR FAIL: no substitutes, ever.
 *
 * WHY THIS FILE EXISTS. The dual-version generation races a Flick (Haiku) and a Whole (Sonnet) call
 * against a 25s client timer that sits under an immutable 29s API Gateway ceiling. Before F-334 the
 * race had two silent substitutes in it: only `flickResult` was captured, so a Whole that came back
 * while Flick was still out was DISCARDED and the teller was bounced back to the conversation; and a
 * Flick-only landing rendered as the story with an apology toast that faded after four seconds.
 * Founder ruling (F-334): a substitute is worse than a failure, because the teller cannot tell it
 * happened. Generation now either COMPLETES or FAILS, and a failure is said plainly.
 *
 * WHY IT IS A SEPARATE HELPER (F4, founder-ratified F-335 item 2). This answers WHICH PROMISES
 * SETTLED. `chooseGeneration` (interviewEngine.js) answers WHETHER THE TEXT IS USABLE. They compose,
 * and both fail closed. The race logic deliberately stays OUT of the Tier 1 engine file, which is
 * byte-mirrored to the voice adapter.
 *
 * Extracted rather than inlined because App.js cannot be loaded in Jest (react-pdf ESM), so a
 * decision left in App.js cannot have a real unit test. Same reason saveGuards.js exists.
 * See .claude/rules/app-multidomain.md.
 */

/**
 * The failure causes. Every failure names one; the alert and the log event carry it.
 *
 * WHO EMITS WHAT — a cause names the LEG THAT BROKE, and naming the wrong leg is the F-416 defect:
 *   pair_timeout   the polish pair never settled inside its budget   (this file, chooseTimeoutOutcome)
 *   empty_output   a call came back carrying no story                (this file + App.js's PIPE-OUT gate)
 *   assembler_fail the ASSEMBLER leg, or the generation path outside the polish pair (App.js catch-all)
 *   polish_timeout the polish resume lost the job or ran out budget  (polishRace.classifyPolishFailure)
 *   polish_fail    the polish call was refused — 429, 400, network   (polishRace.classifyPolishFailure)
 *
 * MIRRORED, and gated: `KNOWN_CAUSES` in `amplify/backend/function/remnantAI/src/alertPayload.js` must
 * hold this same set, or a cause the client sends arrives in the founder's alert as `unknown`.
 * `scripts/fail-causes-drift.test.mjs` fails the build if the two lists diverge.
 */
export const GENERATION_FAIL_CAUSES = ['pair_timeout', 'assembler_fail', 'empty_output', 'polish_timeout', 'polish_fail'];

const hasText = (v) => typeof v === 'string' && v.trim().length > 0;

/**
 * Decide what the teller gets when the polish pair settles (or does not).
 *
 * @param {object} args
 * @param {{text?: string}|null} args.whole  the Whole Tale result, or null if it never settled
 * @param {{text?: string}|null} args.flick  the Flick result, or null if it never settled
 * @returns {{outcome: 'whole'|'fail', degraded: boolean, cause?: string}}
 *
 *   Whole settled with text      -> { outcome: 'whole', degraded: false }   COMPLETE. No apology owed:
 *                                                                          this is the full version,
 *                                                                          whether or not Flick made it.
 *   Whole settled but empty      -> { outcome: 'fail', cause: 'empty_output' }
 *   Whole never settled          -> { outcome: 'fail', cause: 'pair_timeout' }
 *                                   (a Flick-only landing is a SUBSTITUTE and is refused)
 */
export function chooseTimeoutOutcome({ whole, flick } = {}) {
  if (whole && typeof whole === 'object') {
    if (hasText(whole.text)) return { outcome: 'whole', degraded: false };
    // The call came back and carried nothing. That is not a story, and the teller's own raw words
    // must never be dressed up as one (PIPE-OUT-1's intent, extended to the reveal by F-334 2c).
    return { outcome: 'fail', degraded: false, cause: 'empty_output' };
  }
  // Whole never settled inside the race. Flick alone is not a substitute for it.
  void flick;
  return { outcome: 'fail', degraded: false, cause: 'pair_timeout' };
}
